import { useEffect, useState, useCallback } from "react";
import { PageTitle } from "@/components/PageHeader";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { copyText } from "@/lib/clipboard";
import { useDash } from "./ctx";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import FieldProjects from "@/pages/dashboard/fs/FieldProjects";
import WorkOrders from "@/pages/dashboard/fs/WorkOrders";
import VisitQueue from "@/pages/dashboard/fs/VisitQueue";
import Invoices from "@/pages/dashboard/fs/Invoices";
import AccountingSettings from "@/pages/dashboard/fs/AccountingSettings";
import { SendWindowNote, heldToast } from "@/components/fs/SendWindowNote";
import { CollapsibleCard } from "@/components/CollapsibleCard";
import RecurringVendorWizard from "@/pages/dashboard/fs/wizards/RecurringVendor";
import ApproveVendorWizard from "@/pages/dashboard/fs/wizards/ApproveVendor";
import NewJobWizard from "@/pages/dashboard/fs/wizards/NewJob";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import FindVendor from "@/pages/dashboard/FindVendor";
import { ManualAddVendor } from "@/pages/dashboard/fs/ManualAddVendor";
import { MessageQualityHint } from "@/components/MessageQualityHint";
import { smartEncode } from "@/lib/smsQuality";
import {
  HardHat, Plus, Loader2, AlertTriangle, ShieldCheck, ShieldAlert, Clock, CheckCircle2,
  Users, CalendarClock, Send, Camera, RotateCcw, Copy, Trash2, Settings2, X, MapPin, Building2,
  Archive, Check, Globe, FileText, ExternalLink, Search, ChevronDown, ClipboardList, ArrowRight,
} from "lucide-react";
import { V } from "@/lib/vocab";

const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// One source of truth for where a company stands here — the list row and the panel used to disagree.
const linkLabel = (v) => {
  if (v.network_status === "rejected") return "Rejected by Redline";
  if (v.network_status !== "approved") return "Awaiting Redline";
  const s = (v.link || {}).status;
  if (s === "active") return "Active here";
  return "Needs your approval";
};
const CAT_LABEL = {
  common_area_cleaning: "Common-area cleaning", trash: "Trash & garbage", landscaping: "Landscaping",
  plumbing: "Plumbing", electrical: "Electrical", hvac: "HVAC", pest_control: "Pest control",
  pool: "Pool", handyman: "Handyman", janitorial: "Janitorial", other: "Other",
};
const WO_PILL = {
  reported: ["bg-sand-200 text-ink/70", "Reported"],
  dispatched: ["bg-blue-50 text-blue-700", "Sent to crew"],
  scheduled: ["bg-indigo-50 text-indigo-700", "Accepted"],
  enroute: ["bg-indigo-50 text-indigo-700", "On the way"],
  no_response: ["bg-red-50 text-red-700", "No response"],
  overdue: ["bg-orange-50 text-orange-700", "Accepted · overdue"],
  completed: ["bg-emerald-50 text-emerald-700", "Completed"],
  completed_unverified: ["bg-amber-50 text-amber-800", "Done · no photo"],
  closed: ["bg-sand-200 text-ink/50", "Closed"],
  cancelled: ["bg-sand-200 text-ink/40", "Cancelled"],
};
const fmt = (s) => (s ? new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

const Field = ({ label, value }) => (
  <div><dt className="text-xs font-bold uppercase tracking-wider text-ink/40">{label}</dt><dd className="text-ink/80">{value}</dd></div>
);
const addressLine = (v) => {
  const a = v.address_struct || {};
  const parts = [a.street, a.unit, a.city, [a.state, a.zip].filter(Boolean).join(" ").trim()].filter(Boolean);
  return parts.length ? parts.join(", ") : (v.address || "");
};

const Pill = ({ status }) => {
  const [cls, label] = WO_PILL[status] || ["bg-sand-200 text-ink/60", status || "—"];
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide ${cls}`}>{label}</span>;
};

const Num = ({ label, hint, value, onChange, testid, suffix = "min", disabled }) => (
  <label className="flex items-center justify-between gap-3 rounded-xl border border-sand-200 bg-white px-4 py-2.5">
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-ink">{label}</span>
      {hint && <span className="mt-0.5 block text-xs leading-snug text-ink/50">{hint}</span>}
    </span>
    <span className="flex shrink-0 items-center gap-1.5">
      <input type="number" min={0} data-testid={testid} value={value} disabled={disabled}
        onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || "0", 10)))}
        className="w-16 rounded-lg border border-sand-300 px-2 py-1 text-right text-sm outline-none focus:ring-2 focus:ring-redline-500/30 disabled:bg-sand-100 disabled:text-ink/40" />
      <span className="text-xs text-ink/50">{suffix}</span>
    </span>
  </label>
);

const Toggle = ({ checked, onChange, label, desc, testid, disabled }) => (
  <button type="button" role="switch" aria-checked={!!checked} aria-label={label}
    data-state={checked ? "checked" : "unchecked"} data-testid={testid} disabled={disabled}
    onClick={() => onChange(!checked)}
    className="flex w-full items-start gap-3 rounded-xl border border-sand-200 bg-white px-4 py-3 text-left transition-colors hover:border-ink/30 disabled:cursor-not-allowed disabled:opacity-60">
    <span className={`mt-0.5 flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${checked ? "bg-redline-500" : "bg-sand-300"}`}>
      <span className={`h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
    </span>
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-ink">{label}</span>
      {desc && <span className="mt-0.5 block text-xs leading-snug text-ink/50">{desc}</span>}
    </span>
  </button>
);

// ─── the lit-up rail from "See how it works", with this job's real timestamps ──
const JOURNEY = [
  { key: "reported", label: "Reported", Icon: Camera, events: ["reported", "duplicate_merged"] },
  { key: "dispatched", label: "Sent to crew", Icon: Send, events: ["notified", "assigned"] },
  { key: "accepted", label: "Accepted", Icon: Check, events: ["accepted"] },
  { key: "enroute", label: "On site", Icon: MapPin, events: ["enroute"] },
  { key: "completed", label: "Done, with proof", Icon: CheckCircle2, events: ["completed", "completed_no_photo"] },
  { key: "closed", label: "Closed", Icon: Archive, events: ["closed"] },
];
const DONE_THROUGH = {
  reported: 0, dispatched: 1, no_response: 1, scheduled: 2, overdue: 2, enroute: 3,
  completed: 4, completed_unverified: 4, closed: 5, cancelled: 0,
};
const STALLED_STATES = ["no_response", "overdue"];
const ROUTED_LABEL = {
  direct: "sent straight to the crew",
  dispatcher_fallback: "their office handled it — nobody named on site",
  dispatcher_first: "sent to their office first",
  manager_only: "came to you — no vendor could take it",
};

function journeySteps(wo) {
  const tl = wo?.timeline || [];
  const through = DONE_THROUGH[wo?.status] ?? 0;
  const stalled = STALLED_STATES.includes(wo?.status);
  const ended = wo?.status === "closed" || wo?.status === "cancelled";
  return JOURNEY.map((s, i) => {
    const hit = tl.find((e) => s.events.includes(e.event));
    const state = i <= through ? "done" : (i === through + 1 && !ended ? "current" : "todo");
    return { ...s, at: hit?.at || null, state, stalled: stalled && state === "current" };
  });
}

const WoJourney = ({ wo }) => (
  <div data-testid="wo-journey" className="flex overflow-x-auto rounded-xl border border-sand-200 bg-white px-2 py-4">
    {journeySteps(wo).map((s, i, all) => (
      <div key={s.key} className="flex min-w-[104px] flex-1 flex-col items-center text-center">
        <div className="flex w-full items-center">
          <span className={`h-[2px] flex-1 ${i === 0 ? "bg-transparent" : s.state === "todo" ? "bg-sand-200" : "bg-emerald-500"}`} />
          <span data-testid={`wo-step-${s.key}`} data-state={s.state}
            className={`grid h-9 w-9 shrink-0 place-items-center rounded-full border-2 transition-all duration-500 ${
              s.state === "done" ? "border-emerald-500 bg-emerald-500 text-white"
                : s.state === "current"
                  ? (s.stalled ? "animate-pulse border-redline-500 bg-redline-50 text-redline-600"
                    : "border-redline-500 bg-white text-redline-600 shadow-[0_0_0_4px_rgba(211,47,47,0.12)]")
                  : "border-sand-200 bg-sand-50 text-ink/25"}`}>
            <s.Icon className="h-4 w-4" strokeWidth={2.5} />
          </span>
          <span className={`h-[2px] flex-1 ${i === all.length - 1 ? "bg-transparent" : s.state === "done" ? "bg-emerald-500" : "bg-sand-200"}`} />
        </div>
        <div className={`mt-2 text-[11px] font-bold leading-tight ${s.state === "todo" ? "text-ink/30" : "text-ink"}`}>{s.label}</div>
        <div className="text-[10px] leading-tight text-ink/40">
          {s.at ? fmt(s.at) : s.state === "current" ? (s.stalled ? "no answer yet" : "waiting") : ""}
        </div>
      </div>
    ))}
  </div>
);

const WoDots = ({ wo }) => (
  <span className="flex items-center gap-1">
    {journeySteps(wo).map((s) => (
      <span key={s.key} className={`h-1.5 w-1.5 rounded-full ${
        s.state === "done" ? "bg-emerald-500"
          : s.state === "current" ? (s.stalled ? "bg-redline-500" : "animate-pulse bg-redline-400")
            : "bg-sand-300"}`} />
    ))}
  </span>
);

// ─── add a vendor: just a link ───────────────────────────────────────────────
// A manager shouldn't have to pre-create a record or pick a "vendor type" just to send an invite.
// We hand them ONE reusable community apply link — the vendor fills in their own details, adds
// their crew, and can add insurance/W-9 now OR come back to the same link later. You approve
// before any work is sent.
function InviteVendor({ tenantId, communityName = "Your community", onDone, onClose }) {
  const [link, setLink] = useState("");
  const [loadingLink, setLoadingLink] = useState(true);
  const [showSend, setShowSend] = useState(false);
  const [to, setTo] = useState({ phone: "", email: "" });
  const [sendNow, setSendNow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/tenants/${tenantId}/field-services/apply-link`);
        if (alive) setLink(data.link);
      } catch (e) { if (alive) toast.error(e?.response?.data?.detail || "Couldn't load the link"); }
      if (alive) setLoadingLink(false);
    })();
    return () => { alive = false; };
  }, [tenantId]);

  const copy = () => copyText(link, "Apply link copied — send it to any vendor");

  const send = async () => {
    if (!to.phone.trim() && !to.email.trim()) return toast.error("Add a mobile number or an email");
    setBusy(true);
    try {
      const { data } = await api.post(`/tenants/${tenantId}/field-services/apply-link/send`, { ...to, send: sendNow ? "now" : "auto" });
      toast.success(heldToast(data.held, `The link to ${data.sent_to}`) || `Link sent to ${data.sent_to}`);
      setTo({ phone: "", email: "" }); setShowSend(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send it"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <p className="text-sm leading-relaxed text-ink/60">
        Send this link to any vendor you want to add. They fill in their own details and add their
        crew — no account to create. Insurance and W-9 can wait; they can come back to the same link.
        You approve before any work is sent.
      </p>
      <div>
        <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Your vendor apply link</div>
        <div className="flex items-center gap-2">
          <input readOnly data-testid="invite-apply-link" value={loadingLink ? "Loading…" : link}
            className="min-w-0 flex-1 rounded-xl border border-sand-300 bg-sand-50 px-3.5 py-2.5 text-sm text-ink/70 outline-none" />
          <button data-testid="invite-copy-link" onClick={copy} disabled={!link}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-redline-500 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-redline-600 disabled:opacity-50">
            <Copy className="h-4 w-4" /> Copy
          </button>
        </div>
      </div>
      {!showSend ? (
        <button data-testid="invite-show-send" onClick={() => setShowSend(true)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-ink">
          <Send className="h-4 w-4" /> Or text / email it for me
        </button>
      ) : (
        <div className="rounded-2xl border border-sand-200 bg-white p-4 space-y-3" data-testid="invite-send-panel">
          <div className="text-xs font-bold uppercase tracking-wider text-ink/45">Text or email the link</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <input data-testid="invite-send-phone" value={to.phone} placeholder="Mobile number"
              onChange={(e) => setTo({ ...to, phone: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <input data-testid="invite-send-email" value={to.email} placeholder="Email"
              onChange={(e) => setTo({ ...to, email: e.target.value })}
              className="w-full rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
          </div>
          <SendWindowNote tenantId={tenantId} active={showSend} testid="apply-link-window" sendNow={sendNow} onChange={setSendNow} />
          <button data-testid="invite-send-submit" onClick={send} disabled={busy}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-ink px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-ink-700 disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send the link
          </button>
        </div>
      )}
    </div>
  );
}

// ─── open a job by hand: the walk-around, the phone call, the thing you just saw ──

// ─── one vendor: crew, routing, who's on site ───────────────────────────────

function VendorPanel({ tenantId, vendorId, onClose, onChanged, readOnly }) {
  const [v, setV] = useState(null);
  const [crew, setCrew] = useState({ name: "", phone: "", email: "", roles: ["tech"], language: "es" });
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tenants/${tenantId}/field-services/vendors/${vendorId}`);
      setV(data);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? `That vendor is no longer at this ${V.lc("org")}` : "Could not load that vendor");
      onChanged?.(); onClose?.();
    }
  }, [tenantId, vendorId]);
  useEffect(() => { load(); }, [load]);
  if (!v) return <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-ink/30" /></div>;

  const link = v.link || {};
  const sa = v.site_assignment || {};
  const techs = (v.contacts || []).filter((c) => (c.roles || []).includes("tech") && c.active !== false);

  const addCrew = async () => {
    if (!crew.name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      await api.post(`/tenants/${tenantId}/field-services/vendors/${vendorId}/contacts`, crew);
      toast.success(`${crew.name} invited — they verify their own phone and pick their language`);
      setCrew({ name: "", phone: "", email: "", roles: ["tech"], language: "es" });
      await load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add them"); }
    setBusy(false);
  };
  const saveRouting = async (patch) => {
    if (readOnly) return toast.error(`${V.many("board")} have read-only access here.`);
    const body = {
      categories: link.categories || [], in_scope_categories: link.in_scope_categories || [],
      dispatch_mode: link.dispatch_mode || "direct_then_dispatcher",
      site_assignment: {
        category_scope: sa.category_scope || [], primary_contact_id: sa.primary_contact_id || null,
        backup_contact_id: sa.backup_contact_id || null, coverage: sa.coverage || [],
      }, ...patch,
    };
    try {
      const { data } = await api.put(`/tenants/${tenantId}/field-services/vendors/${vendorId}/assignment`, body);
      setV(data); onChanged?.();
      toast.success("Saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save that"); }
  };

  const removeVendor = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/tenants/${tenantId}/field-services/vendors/${vendorId}`);
      toast.success(`${data.vendor} removed from this ${V.lc("org")}${data.unassigned ? ` · ${data.unassigned} open job${data.unassigned === 1 ? "" : "s"} freed` : ""}`);
      onChanged?.(); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove them"); }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="vendor-panel">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-heading text-lg font-bold text-ink">{v.name}</div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink/50">
            <span className="rounded-full bg-sand-100 px-2 py-0.5 font-semibold uppercase tracking-wide"
              data-testid="vendor-panel-status">
              {linkLabel(v)}
            </span>
            {v.paperwork?.ok
              ? <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" />Cleared to bid & work · insured thru {(v.coi || {}).expires_on}</span>
              : <span className="inline-flex items-center gap-1 text-amber-700" data-testid="vendor-paperwork-gap">
                <ShieldAlert className="h-3.5 w-3.5" />
                Can be told about work, can't take a job — needs {(v.paperwork?.missing || []).map((m) => m.label.toLowerCase()).join(", ")}
              </span>}
            <span>{v.routable_crew} verified crew</span>
            {v.live_work_orders > 0 && <span className="font-semibold text-ink/60">{v.live_work_orders} live job(s)</span>}
            {v.referred_here && (
              <span className="rounded-full bg-indigo-50 px-2 py-0.5 font-semibold text-indigo-700" data-testid="vendor-referred">
                We already use them
              </span>
            )}
            {v.property_count > 1 && (
              <span className="inline-flex items-center gap-1 font-semibold text-ink/60">
                <Building2 className="h-3.5 w-3.5" />Serves {v.property_count} properties
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {v.network_status !== "approved" && !v.referred_here ? (
            <span data-testid="vendor-awaiting-network" className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold ${v.network_status === "rejected" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"}`}>
              <Clock className="h-3.5 w-3.5" /> {v.network_status === "rejected" ? "Rejected by Redline" : "Awaiting Redline approval"}
            </span>
          ) : (v.link || {}).status !== "active" ? (
            <button data-testid="vendor-approve-btn" onClick={() => setApproveOpen(true)}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700">
              Approve for this {V.lc("org")}
            </button>
          ) : (
            <span className="inline-flex items-center gap-1.5 rounded-xl bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-700">
              <CheckCircle2 className="h-3.5 w-3.5" /> Approved here
            </span>
          )}
          {(v.status === "pending_approval" && !((v.coi || {}).expires_on)) && (
            <span data-testid="vendor-docs-outstanding" className="inline-flex items-center gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800">
              <AlertTriangle className="h-3.5 w-3.5" /> Insurance outstanding
            </span>
          )}
          {v.invite_held && (
            <span data-testid="vendor-invite-held" className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-800">
              <Clock className="h-3.5 w-3.5" /> Invite goes out {v.invite_held.send_at_local}
            </span>
          )}
          {!readOnly && v.invite_held && (
            <button data-testid="vendor-invite-send-now" disabled={busy} onClick={async () => {
              setBusy(true);
              try {
                await api.post(`/tenants/${tenantId}/field-services/vendors/${vendorId}/send-held-invite`);
                toast.success(`Invite sent to ${v.invite_held.to || v.name}`);
                await load(); onChanged?.();
              } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send it"); }
              setBusy(false);
            }} className="inline-flex items-center gap-1.5 rounded-xl bg-ink px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">
              <Send className="h-3.5 w-3.5" /> Send it now
            </button>
          )}
          {!readOnly && (v.status === "pending_approval" && !((v.coi || {}).expires_on)) && (
            <button data-testid="vendor-remind-docs" onClick={async () => {
              try {
                const { data } = await api.post(`/tenants/${tenantId}/field-services/vendors/${vendorId}/remind-docs`);
                toast.success(data.sent ? "Reminder sent — we texted/emailed them their link" : "No vendor contact to remind yet");
              } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send the reminder"); }
            }} className="inline-flex items-center gap-1.5 rounded-xl border border-amber-300 px-3 py-2 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-50">
              <Send className="h-3.5 w-3.5" /> Send reminder
            </button>
          )}
          {!readOnly && v.portal_link && (
            <button data-testid="vendor-copy-portal" onClick={() => copyText(v.portal_link, "Vendor link copied")}
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-4 py-2 text-sm font-semibold text-ink/70 hover:border-ink/30">
              <Copy className="h-3.5 w-3.5" /> Their link
            </button>
          )}
          {!readOnly && (
            removing ? (
              <span className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 bg-red-50 px-2.5 py-1.5 text-xs">
                <span className="font-semibold text-red-700">Remove &amp; free open jobs?</span>
                <button data-testid="vendor-remove-confirm" onClick={removeVendor} disabled={busy}
                  className="rounded-lg bg-red-600 px-2.5 py-1 font-bold text-white hover:bg-red-700 disabled:opacity-50">Remove</button>
                <button data-testid="vendor-remove-cancel" onClick={() => setRemoving(false)}
                  className="rounded-lg px-2 py-1 font-semibold text-ink/60">Cancel</button>
              </span>
            ) : (
              <button data-testid="vendor-remove-btn" onClick={() => setRemoving(true)}
                className="inline-flex items-center gap-1.5 rounded-xl border border-red-200 px-3 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            )
          )}
        </div>
      </div>

      {v.network_status === "approved" && (
        <button data-testid="vendor-referral-toggle" onClick={async () => {
          try {
            await api.put(`/tenants/${tenantId}/field-services/vendors/${vendorId}/referral`, { enabled: !v.referral_listed });
            toast.success(v.referral_listed ? `Removed from ${V.lc("person")} referrals` : `Now recommended to ${V.lcs("person")}`);
            await load(); onChanged?.();
          } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
        }} disabled={readOnly}
          className={`flex w-full items-center justify-between gap-3 rounded-2xl border p-4 text-left transition-colors ${v.referral_listed ? "border-emerald-200 bg-emerald-50" : "border-sand-200 bg-white hover:border-ink/20"} ${readOnly ? "cursor-default opacity-70" : ""}`}>
          <div className="flex items-center gap-2.5">
            <Users className={`h-5 w-5 ${v.referral_listed ? "text-emerald-600" : "text-ink/40"}`} />
            <div>
              <div className="text-sm font-bold text-ink">Recommend to {V.lcs("person")}</div>
              <div className="text-xs text-ink/55">List this company in the concierge's referrals so {V.lcs("person")} can be pointed to them.</div>
            </div>
          </div>
          <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${v.referral_listed ? "bg-emerald-600 text-white" : "bg-sand-200 text-ink/60"}`}>
            {v.referral_listed ? "Listed" : "Off"}
          </span>
        </button>
      )}

      {/* full application — review before approving */}
      <div data-testid="vendor-application" className="rounded-2xl border border-sand-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/45">
          <FileText className="h-3.5 w-3.5" /> Application
        </div>
        {(v.coi_flags || []).length > 0 && (
          <div data-testid="vendor-coi-flags" className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3">
            <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-900">
              <AlertTriangle className="h-3.5 w-3.5" /> Check before approving
            </div>
            {(v.coi_flags || []).map((fl, i) => (
              <div key={i} className="text-xs font-semibold text-amber-900">• {fl.message}</div>
            ))}
          </div>
        )}
        <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
          <Field label="Legal name" value={v.name} />
          {v.dba && <Field label="DBA" value={v.dba} />}
          {v.ein && <Field label="EIN / Tax ID" value={v.ein} />}
          {v.phone && <Field label="Phone" value={v.phone} />}
          {v.email && <Field label="Billing email" value={v.email} />}
          {v.website && <Field label="Website" value={<a className="text-redline-600 underline" href={/^https?:/.test(v.website) ? v.website : `https://${v.website}`} target="_blank" rel="noreferrer">{v.website}</a>} />}
          {addressLine(v) && <Field label="Address" value={addressLine(v)} />}
          {(v.tax || {}).classification && <Field label="Tax classification" value={v.tax.classification} />}
          {(v.markets || []).length > 0 && <Field label="Serves" value={v.markets.join(", ")} />}
        </dl>
        {(v.website_profile || {}).summary && (
          <div className="mt-3 rounded-xl bg-sand-50 p-3 text-sm text-ink/70">
            <div className="mb-1 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/40"><Globe className="h-3.5 w-3.5" /> About</div>
            {v.website_profile.summary}
          </div>
        )}
        {(v.licenses || []).length > 0 && (
          <div className="mt-3">
            <div className="mb-1 text-xs font-bold uppercase tracking-wider text-ink/40">Licenses</div>
            {v.licenses.map((l, i) => (
              <div key={i} data-testid={`vendor-license-${i}`} className="text-sm text-ink/70">
                {[l.type, l.number && `#${l.number}`, l.state, l.expires_on && `exp ${l.expires_on}`].filter(Boolean).join(" · ")}
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          {v.coi_file_url ? (
            <a data-testid="vendor-coi-view" href={v.coi_file_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-semibold text-ink/70 hover:border-ink/30">
              <ShieldCheck className="h-3.5 w-3.5" /> View certificate{(v.coi || {}).expires_on ? ` · exp ${v.coi.expires_on}` : ""} <ExternalLink className="h-3 w-3" />
            </a>
          ) : <span className="text-xs font-semibold text-redline-600">No certificate on file</span>}
          {v.w9_file_url && (
            <a data-testid="vendor-w9-view" href={v.w9_file_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-semibold text-ink/70 hover:border-ink/30">
              <FileText className="h-3.5 w-3.5" /> View W-9 <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
        {(v.agreement || {}).signed && (
          <div className="mt-3 rounded-xl bg-sand-50 p-3 text-xs text-ink/60" data-testid="vendor-agreement-info">
            <span className="font-semibold text-ink/70">Agreement signed</span> by {v.agreement.signed_by} · {fmt(v.agreement.signed_at)} · {v.agreement.version}
          </div>
        )}
        {!readOnly && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button data-testid="vendor-verify-coi" onClick={async () => {
              const exp = window.prompt("Expiry date printed on the certificate (YYYY-MM-DD)",
                (v.coi || {}).expires_on || "");
              if (!exp) return;
              const lim = window.prompt("General liability limit on the certificate (numbers only)",
                String((v.coi || {}).gl_limit || "")) || "";
              try {
                const { data } = await api.post(
                  `/tenants/${tenantId}/field-services/vendors/${vendorId}/verify-coi`,
                  { expires_on: exp, gl_limit: lim || undefined, addl_insured: true });
                toast.success(data.work_orders_rechecked
                  ? `Verified — ${data.work_orders_rechecked} job(s) re-checked`
                  : "Verified");
                await load(); onChanged?.();
              } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save that"); }
            }} className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/70 hover:border-ink/30">
              <ShieldCheck className="h-3.5 w-3.5" /> I've checked the certificate
            </button>
            <button data-testid="vendor-refer" onClick={async () => {
              try {
                const { data } = await api.post(
                  `/tenants/${tenantId}/field-services/vendors/${vendorId}/refer`,
                  { referred: !v.referred_here });
                toast.success(data.referred
                  ? "Fast-tracked — they can bid and be dispatched here without waiting on Redline's review"
                  : "No longer fast-tracked here");
                await load(); onChanged?.();
              } catch (e) { toast.error(e?.response?.data?.detail || "Try again"); }
            }} className="inline-flex items-center gap-1.5 rounded-xl border border-sand-300 px-3 py-1.5 text-xs font-bold text-ink/70 hover:border-ink/30">
              {v.referred_here ? "Remove the fast-track" : "We already use them (fast-track)"}
            </button>
          </div>
        )}
      </div>

      {/* crew */}
      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/45">
          <Users className="h-3.5 w-3.5" /> Crew & office
        </div>
        <div className="space-y-2">
          {(v.contacts || []).filter((c) => c.active !== false).map((c) => (
            <div key={c.id} data-testid={`crew-row-${c.id}`}
              className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sand-200 bg-white px-4 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-ink">{c.name}
                  <span className="ml-2 text-xs font-normal text-ink/45">{(c.roles || []).join(" · ")}</span>
                  <span className="ml-2 rounded bg-sand-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-ink/50">{c.preferred_language === "es" ? "Español" : "English"}</span>
                </div>
                <div className="text-xs text-ink/45">{c.phone || c.email}</div>
              </div>
              <div className="flex items-center gap-2">
                {c.verify_status === "verified" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />Verified</span>}
                {c.verify_status === "invited" && (
                  <>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><AlertTriangle className="h-3.5 w-3.5" />Pending — not routable</span>
                    {!readOnly && (
                      <button data-testid={`crew-resend-${c.id}`} onClick={async () => {
                        await api.post(`/tenants/${tenantId}/field-services/vendors/${vendorId}/contacts/${c.id}/resend`);
                        toast.success("Invite resent");
                      }} className="inline-flex items-center gap-1 rounded-lg border border-sand-300 px-2.5 py-1 text-xs font-semibold hover:border-ink/30">
                        <RotateCcw className="h-3 w-3" /> Resend
                      </button>
                    )}
                  </>
                )}
                {c.verify_status === "stopped" && <span className="text-xs font-semibold text-danger-600">Opted out of texts</span>}
                {!readOnly && (
                  <button data-testid={`crew-remove-${c.id}`} onClick={async () => {
                    await api.delete(`/tenants/${tenantId}/field-services/vendors/${vendorId}/contacts/${c.id}`);
                    toast.success(`${c.name} removed`); await load(); onChanged?.();
                  }} className="rounded-lg p-1.5 text-ink/30 hover:bg-danger-50 hover:text-danger-600"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
        {!readOnly && (
        <div className="mt-3 rounded-xl border border-dashed border-sand-300 bg-sand-50/60 p-4">
          <div className="mb-2 text-xs font-semibold text-ink/60">Add someone for them (they get their own invite, verify their own phone and pick their own language)</div>
          <div className="grid gap-2 sm:grid-cols-4">
            <input data-testid="add-crew-name" value={crew.name} placeholder="Name" onChange={(e) => setCrew({ ...crew, name: e.target.value })}
              className="rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <input data-testid="add-crew-phone" value={crew.phone} placeholder="Mobile" onChange={(e) => setCrew({ ...crew, phone: e.target.value })}
              className="rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
            <select data-testid="add-crew-role" value={crew.roles[0]} onChange={(e) => setCrew({ ...crew, roles: [e.target.value] })}
              className="rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none">
              {(v.available_roles || [
                { key: "tech", label: "Field crew" },
                { key: "dispatcher", label: "Office / dispatcher" },
                { key: "billing", label: "Billing" },
              ]).map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <button data-testid="add-crew-submit" onClick={addCrew} disabled={busy}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white hover:bg-ink/90 disabled:opacity-50">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add
            </button>
          </div>
        </div>
        )}
      </div>

      {/* routing */}
      <div>
        <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/45">
          <MapPin className="h-3.5 w-3.5" /> Who's on site, and where jobs go
        </div>
        <div className="space-y-3 rounded-xl border border-sand-200 bg-white p-4">
          <fieldset disabled={readOnly} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-ink/60">Primary on-site person</span>
              <select data-testid="sa-primary" value={sa.primary_contact_id || ""} disabled={readOnly}
                onChange={(e) => saveRouting({ site_assignment: { ...sa, primary_contact_id: e.target.value || null } })}
                className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none disabled:bg-sand-100 disabled:text-ink/40">
                <option value="">— nobody assigned (jobs go to the office) —</option>
                {techs.map((c) => <option key={c.id} value={c.id}>{c.name}{c.verify_status !== "verified" ? " (not verified yet)" : ""}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-ink/60">Backup</span>
              <select data-testid="sa-backup" value={sa.backup_contact_id || ""} disabled={readOnly}
                onChange={(e) => saveRouting({ site_assignment: { ...sa, backup_contact_id: e.target.value || null } })}
                className="w-full rounded-xl border border-sand-300 px-3 py-2 text-sm outline-none disabled:bg-sand-100 disabled:text-ink/40">
                <option value="">— none —</option>
                {techs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
          </div>
          <div>
            <div className="mb-1.5 text-xs font-semibold text-ink/60">In scope here — anything else goes to the office and to you, never straight to the crew</div>
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(CAT_LABEL).map(([k, label]) => {
                const on = (link.in_scope_categories || []).includes(k);
                return (
                  <button key={k} data-testid={`scope-${k}`} disabled={readOnly} onClick={() => saveRouting({
                    in_scope_categories: on ? (link.in_scope_categories || []).filter((x) => x !== k) : [...(link.in_scope_categories || []), k],
                  })} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${on ? "bg-emerald-600 text-white" : "bg-sand-100 text-ink/60 hover:bg-sand-200"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <div className="mb-1.5 text-xs font-semibold text-ink/60">Coverage — when is that person actually on site?</div>
            {(sa.coverage || []).map((w, i) => (
              <div key={i} className="mb-2 flex flex-wrap items-center gap-2">
                <div className="flex gap-1">
                  {DOW.map((d, di) => {
                    const on = (w.dow || []).includes(di);
                    return (
                      <button key={d} data-testid={`cov-${i}-${di}`} onClick={() => {
                        const cov = [...(sa.coverage || [])];
                        cov[i] = { ...w, dow: on ? (w.dow || []).filter((x) => x !== di) : [...(w.dow || []), di] };
                        saveRouting({ site_assignment: { ...sa, coverage: cov } });
                      }} className={`h-8 w-9 rounded-lg text-[11px] font-bold transition-colors ${on ? "bg-ink text-white" : "bg-sand-100 text-ink/50"}`}>{d}</button>
                    );
                  })}
                </div>
                <input type="time" data-testid={`cov-${i}-start`} value={w.start || "06:00"} onChange={(e) => {
                  const cov = [...(sa.coverage || [])]; cov[i] = { ...w, start: e.target.value };
                  saveRouting({ site_assignment: { ...sa, coverage: cov } });
                }} className="rounded-lg border border-sand-300 px-2 py-1.5 text-sm" />
                <span className="text-xs text-ink/40">to</span>
                <input type="time" data-testid={`cov-${i}-end`} value={w.end || "14:00"} onChange={(e) => {
                  const cov = [...(sa.coverage || [])]; cov[i] = { ...w, end: e.target.value };
                  saveRouting({ site_assignment: { ...sa, coverage: cov } });
                }} className="rounded-lg border border-sand-300 px-2 py-1.5 text-sm" />
                <button onClick={() => saveRouting({ site_assignment: { ...sa, coverage: (sa.coverage || []).filter((_, j) => j !== i) } })}
                  className="rounded-lg p-1.5 text-ink/30 hover:text-redline-600"><X className="h-3.5 w-3.5" /></button>
              </div>
            ))}
            <button data-testid="cov-add" disabled={readOnly} onClick={() => saveRouting({
              site_assignment: { ...sa, coverage: [...(sa.coverage || []), { dow: [0, 1, 2, 3, 4, 5], start: "06:00", end: "14:00" }] },
            })} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-sand-300 px-3 py-1.5 text-xs font-semibold text-ink/60 hover:border-ink/30">
              <Plus className="h-3 w-3" /> Add a window
            </button>
            <p className="mt-2 text-xs leading-relaxed text-ink/45">
              Outside these hours a report goes to the office first instead of waking someone up.
              Leave it empty to route directly around the clock.
            </p>
          </div>
          </fieldset>
        </div>
      </div>
      <button onClick={onClose} className="text-sm font-semibold text-ink/50 hover:text-ink">Close</button>

      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent className="max-w-3xl" data-testid="approve-vendor-dialog">
          <DialogHeader>
            <DialogTitle>Approve {v.name}</DialogTitle>
            <DialogDescription>Read their paperwork and say which trades they own at this property.</DialogDescription>
          </DialogHeader>
          {approveOpen && (
            <ApproveVendorWizard tenantId={tenantId} vendorId={vendorId} vendor={v}
              onDone={async () => { await load(); onChanged?.(); }}
              onClose={() => setApproveOpen(false)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────
// Applications that have stalled — the case nothing used to chase at all. Same signpost pattern
// as Work orders: one amber line that stays until someone acts. "Awaiting your approval" has its
// own callout below, so this deliberately doesn't repeat it.
const VendorSetupStrip = ({ vendors, onOpen }) => {
  const half = vendors.filter((v) => ["invited", "pending_approval"].includes(v.status));
  const gone = half.filter((v) => v.setup_stale_at);
  if (!half.length) return null;
  const warn = gone.length > 0;
  return (
    <button data-testid="vendor-setup-strip" onClick={() => onOpen?.((gone[0] || half[0]).id)}
      className={`flex w-full items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-left transition-colors ${warn
        ? "border-amber-200 bg-amber-50/70 hover:border-amber-300" : "border-sand-200 bg-white hover:border-ink/25"}`}>
      <Clock className={`mt-0.5 h-4 w-4 shrink-0 ${warn ? "text-amber-600" : "text-ink/35"}`} />
      <span className="min-w-0">
        <span className="block text-xs font-bold text-ink">
          {half.length} compan{half.length === 1 ? "y" : "ies"} part-way through setup
        </span>
        <span className="block text-[11px] leading-snug text-ink/55">
          {gone.length
            ? `${gone.length} stopped responding after four reminders — we've stopped asking. Chase them yourself or archive them.`
            : "Their office is reminded automatically on day 1, 3, 7 and 14, naming exactly what's outstanding."}
        </span>
      </span>
      <ArrowRight className="ml-auto mt-0.5 h-3.5 w-3.5 shrink-0 text-ink/25" />
    </button>
  );
};

export default function FieldServices() {
  const { tenantId, role, tenant, refreshTenants } = useDash();
  const readOnly = role === "board_member";
  const moduleLabel = (tenant?.field_services_label || "").trim() || "Field Services";
  // Allow deep-linking to a specific tab, e.g. the Settings ⚙ on the Capabilities card
  // links to /dashboard/field-services?tab=settings.
  const [searchParams] = useSearchParams();
  const _initialTab = ["spine", "queue", "work", "today", "vendors", "find", "projects", "invoices",
    "settings"].includes(searchParams.get("tab")) ? searchParams.get("tab") : "spine";
  const [tab, setTab] = useState(_initialTab);
  const [woStatus, setWoStatus] = useState("open");
  const [ov, setOv] = useState(null);
  const [wos, setWos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [openVendor, setOpenVendor] = useState(() => searchParams.get("vendor") || null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [vendorQ, setVendorQ] = useState("");
  const shownVendors = vendors.filter((v) => {
    const term = vendorQ.trim().toLowerCase();
    if (!term) return true;
    return (v.name || "").toLowerCase().includes(term)
      || (v.trades || []).some((t) => (CAT_LABEL[t] || t).toLowerCase().includes(term));
  });
  const [newJobOpen, setNewJobOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schedOpen, setSchedOpen] = useState(false);
  const [pendingNewProject, setPendingNewProject] = useState(false);
  const [woRefresh, setWoRefresh] = useState(0);
  const [glCodes, setGlCodes] = useState([]);

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [o, w, v, s, g] = await Promise.all([
        api.get(`/tenants/${tenantId}/field-services/overview`),
        api.get(`/tenants/${tenantId}/field-services/work-orders?status=${woStatus === "all" ? "" : woStatus}&limit=50`),
        api.get(`/tenants/${tenantId}/field-services/vendors`),
        api.get(`/tenants/${tenantId}/field-services/schedules`),
        api.get(`/tenants/${tenantId}/field-services/gl-codes`).catch(() => ({ data: { codes: [] } })),
      ]);
      setOv(o.data); setWos(w.data.work_orders || []); setVendors(v.data.vendors || []);
      setSchedules(s.data.schedules || []); setCfg(o.data.config);
      setGlCodes(g.data.codes || []);
    } catch (e) {
      if (e?.response?.status === 402 || e?.response?.status === 403) setOv({ gated: e.response.data.detail });
      else toast.error("Could not load Field Services");
    }
    setLoading(false);
  }, [tenantId, woStatus]);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    try { await api.put(`/tenants/${tenantId}/field-services/config`, patch); }
    catch { toast.error("Could not save that setting"); }
  };
  const woOp = async (id, op, body) => {
    try {
      await api.post(`/tenants/${tenantId}/field-services/work-orders/${id}/${op}`, body || {});
      toast.success("Done"); setDetail(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-ink/30" /></div>;
  if (ov?.gated) return (
    <div className="mx-auto max-w-lg py-16 text-center" data-testid="fs-gated">
      <HardHat className="mx-auto h-8 w-8 text-ink/25" />
      <h1 className="mt-4 font-heading text-2xl font-bold text-ink">{moduleLabel}</h1>
      <p className="mt-2 text-sm text-ink/55">{ov.gated}</p>
    </div>
  );

  const TABS = [["spine", "Work orders"], ["queue", "Visit queue"], ["today", "Today & schedules"],
    ["work", "Crew & dispatch"], ["projects", "Projects"], ["invoices", "Invoices"],
    ["vendors", "Vendors"], ["settings", "Settings"]];
  const awaitingApproval = vendors.filter((v) => v.network_status === "approved" && (v.link || {}).status === "pending_approval");
  const WO_FILTERS = [["open", "Open"], ["completed,completed_unverified", "Completed"],
    ["completed_unverified", "Done · no photo"], ["closed", "Closed"], ["all", "Everything"]];

  // Group the overview's soft warnings by vendor so we can fold them into each vendor's card
  // (instead of a disruptive stack of banners at the top). `attentionCount` = how many distinct
  // vendors have anything outstanding (insurance, pending/opted-out crew, stale roster).
  const warningsByVendor = {};
  for (const w of (ov?.warnings || [])) {
    if (!w.vendor_id) continue;
    (warningsByVendor[w.vendor_id] ||= []).push(w);
  }
  const attentionCount = Object.keys(warningsByVendor).length;

  return (
    <div className="space-y-6" data-testid="field-services-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <PageTitle title={moduleLabel} about="Requests go straight to the person on site. The office is copied, and only steps in if nobody answers." />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="fs-new-menu"
                className="inline-flex items-center gap-2 rounded-xl bg-redline-500 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-redline-600">
                <Plus className="h-4 w-4" /> Start something
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              {(!readOnly || ov?.board_can_open_jobs) && (
                <DropdownMenuItem data-testid="fs-new-job" className="cursor-pointer items-start gap-2.5 py-2.5"
                  onClick={() => setNewJobOpen(true)}>
                  <HardHat className="mt-0.5 h-4 w-4 shrink-0 text-redline-500" />
                  <span>
                    <span className="block text-sm font-bold text-ink">A job</span>
                    <span className="block text-xs leading-snug text-ink/55">Something needs fixing now — usually someone on the phone.</span>
                  </span>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem data-testid="fs-new-project" className="cursor-pointer items-start gap-2.5 py-2.5"
                onClick={() => { setTab("projects"); setPendingNewProject(true); }}>
                <ClipboardList className="mt-0.5 h-4 w-4 shrink-0 text-redline-500" />
                <span>
                  <span className="block text-sm font-bold text-ink">A project, out for bids</span>
                  <span className="block text-xs leading-snug text-ink/55">A bigger piece of work — write a scope and get prices.</span>
                </span>
              </DropdownMenuItem>
              {!readOnly && (
                <DropdownMenuItem data-testid="fs-new-recurring" className="cursor-pointer items-start gap-2.5 py-2.5"
                  onClick={() => { setTab("today"); setSchedOpen(true); }}>
                  <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-redline-500" />
                  <span>
                    <span className="block text-sm font-bold text-ink">A recurring service</span>
                    <span className="block text-xs leading-snug text-ink/55">A company that comes on set days — cleaning, landscaping, trash.</span>
                  </span>
                </DropdownMenuItem>
              )}
              {!readOnly && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid="invite-vendor-btn" className="cursor-pointer items-start gap-2.5 py-2.5"
                    onClick={() => setInviteOpen(true)}>
                    <Users className="mt-0.5 h-4 w-4 shrink-0 text-ink/45" />
                    <span>
                      <span className="block text-sm font-bold text-ink">Invite a company</span>
                      <span className="block text-xs leading-snug text-ink/55">Bring a vendor you already use onto this property.</span>
                    </span>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {attentionCount > 0 && tab !== "vendors" && (
        <button data-testid="fs-attention-line" onClick={() => setTab("vendors")}
          className="flex w-full items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-left transition-colors hover:border-amber-300">
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="text-sm font-semibold text-ink/80">
            {attentionCount} vendor{attentionCount === 1 ? "" : "s"} need{attentionCount === 1 ? "s" : ""} attention
          </span>
          <span className="ml-auto text-xs font-semibold text-amber-700 underline underline-offset-2">Review in Vendors</span>
        </button>
      )}

      <div className="sm:hidden">
        <label className="mb-1 block text-xs font-bold uppercase tracking-wider text-ink/40">Section</label>
        <select data-testid="fs-tab-picker" value={tab} onChange={(e) => setTab(e.target.value)}
          className="w-full rounded-xl border border-sand-300 bg-white px-3 py-3 text-sm font-bold text-ink outline-none">
          {TABS.map(([k, label]) => (
            <option key={k} value={k}>
              {label}{k === "vendors" && attentionCount > 0 ? ` (${attentionCount} need attention)` : ""}
            </option>
          ))}
        </select>
      </div>

      <div className="hidden gap-1.5 overflow-x-auto border-b border-sand-200 sm:flex">
        {TABS.map(([k, label]) => (
          <button key={k} data-testid={`fs-tab-${k}`} onClick={() => setTab(k)}
            className={`-mb-px inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-bold transition-colors ${tab === k ? "border-redline-500 text-ink" : "border-transparent text-ink/45 hover:text-ink/70"}`}>
            {label}
            {k === "vendors" && attentionCount > 0 && (
              <span data-testid="fs-vendors-attention-badge"
                className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[10px] font-bold leading-5 text-white">
                {attentionCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "work" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[["Waiting on a crew", (ov?.counts?.reported || 0) + (ov?.counts?.dispatched || 0) + (ov?.counts?.scheduled || 0) + (ov?.counts?.enroute || 0)],
              ["No response", ov?.counts?.no_response || 0],
              ["Accepted · overdue", ov?.counts?.overdue || 0],
              ["Done, no photo", ov?.counts?.completed_unverified || 0]].map(([label, n]) => (
              <div key={label} className="rounded-xl border border-sand-200 bg-white px-4 py-3">
                <div className="font-heading text-2xl font-extrabold text-ink">{n}</div>
                <div className="text-xs font-semibold text-ink/50">{label}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="wo-filters">
            {WO_FILTERS.map(([k, label]) => (
              <button key={k} data-testid={`wo-filter-${k}`} onClick={() => setWoStatus(k)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${
                  woStatus === k ? "bg-ink text-white" : "bg-sand-100 text-ink/55 hover:bg-sand-200"}`}>
                {label}
              </button>
            ))}
          </div>
          {wos.length === 0 && (
            <div className="rounded-xl border border-dashed border-sand-300 py-14 text-center text-sm text-ink/45" data-testid="wo-empty">
              {woStatus === "open"
                ? `Nothing open. Send ${V.a("person")}'s request out from the Requests page, take one by phone, or use “New job” above.`
                : "Nothing here yet with that status."}
            </div>
          )}
          {wos.map((a) => (
            <button key={a.id} data-testid={`wo-row-${a.id}`} onClick={async () => {
              const { data } = await api.get(`/tenants/${tenantId}/field-services/work-orders/${a.id}`);
              setDetail(data);
            }} className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-sand-200 bg-white px-4 py-3 text-left transition-colors hover:border-ink/25">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-bold text-ink/40">{a.wo.ref}</span>
                  <Pill status={a.wo.status} />
                  <WoDots wo={a.wo} />
                  {(a.wo.flags || []).includes("out_of_scope") && <span className="rounded-full bg-redline-50 px-2 py-0.5 text-[10px] font-bold uppercase text-redline-700">Out of scope</span>}
                </div>
                <div className="mt-1 truncate text-sm font-semibold text-ink">{a.wo.summary}</div>
                <div className="text-xs text-ink/45">
                  {a.wo.location || "no location"} · {a.wo.vendor_name || "no vendor"}
                  {a.wo.tech_name ? ` · ${a.wo.tech_name}` : ""} · {fmt(a.created_at)}
                </div>
              </div>
              <div className="text-xs font-semibold text-ink/40">{ROUTED_LABEL[a.wo.routed_via] || (a.wo.routed_via ? "not dispatched yet" : "")}</div>
            </button>
          ))}
        </div>
      )}

      {tab === "vendors" && (
        <div className="space-y-3">
          <VendorSetupStrip vendors={vendors} onOpen={setOpenVendor} />
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
            <div className="text-xs font-semibold text-ink/45">
              {vendorQ.trim()
                ? `${shownVendors.length} of ${vendors.length} shown`
                : `${vendors.length} vendor${vendors.length === 1 ? "" : "s"} at this ${V.lc("org")}`}
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink/30" />
                <input data-testid="vendors-search" value={vendorQ} onChange={(e) => setVendorQ(e.target.value)}
                  placeholder="Search this list"
                  className="w-full rounded-xl border border-sand-300 bg-white py-2 pl-8 pr-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30 sm:w-56" />
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="vendors-add-manual-cta" onClick={() => setManualOpen(true)}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl border border-sand-300 bg-white px-3.5 py-2 text-sm font-bold text-ink/75 transition-colors hover:border-ink/30 sm:flex-none">
                  <Plus className="h-4 w-4 text-redline-500" /> Add manually
                </button>
                <button data-testid="vendors-browse-cta" onClick={() => setTab("find")}
                  className="inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-xl bg-redline-500 px-3.5 py-2 text-sm font-bold text-white hover:bg-redline-600 sm:flex-none">
                  <Plus className="h-4 w-4" /> Browse network
                </button>
              </div>
            </div>
          </div>
          {awaitingApproval.length > 0 && (
            <div data-testid="awaiting-approval-callout" className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
              <div className="text-sm font-semibold text-amber-900">
                <span className="font-bold">{awaitingApproval.length}</span> vendor{awaitingApproval.length === 1 ? "" : "s"} awaiting your approval
              </div>
              <button data-testid="awaiting-open-first" onClick={() => setOpenVendor(awaitingApproval[0].id)}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700">Review</button>
            </div>
          )}
          {vendors.length === 0 && (
            <div className="rounded-xl border border-dashed border-sand-300 py-14 text-center text-sm text-ink/45" data-testid="vendors-empty">
              No vendors here yet. <button onClick={() => setTab("find")} className="font-bold text-redline-600 underline">Browse the approved network</button> to add one, or invite a company you already work with.
            </div>
          )}
          {vendors.length > 0 && shownVendors.length === 0 && (
            <div className="rounded-xl border border-dashed border-sand-300 py-14 text-center text-sm text-ink/45" data-testid="vendors-no-match">
              Nothing here matches &ldquo;{vendorQ.trim()}&rdquo;.{" "}
              <button onClick={() => setVendorQ("")} className="font-bold text-redline-600 underline">Clear the search</button>{" "}
              or <button onClick={() => setTab("find")} className="font-bold text-redline-600 underline">look across the network</button>.
            </div>
          )}
          {shownVendors.map((v) => (
            <button key={v.id} data-testid={`vendor-row-${v.id}`} onClick={() => setOpenVendor(v.id)}
              className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-sand-200 bg-white px-4 py-3 text-left transition-colors hover:border-ink/25">
              <div>
                <div className="text-sm font-bold text-ink">{v.name}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-ink/45">
                  <span data-testid={`vendor-row-status-${v.id}`} className={`rounded-full px-2 py-0.5 font-semibold uppercase tracking-wide ${
                    v.network_status === "rejected" ? "bg-red-50 text-red-700"
                      : v.network_status === "approved" && (v.link || {}).status !== "active" && (v.link || {}).status !== "invited"
                        ? "bg-amber-100 text-amber-800"
                        : v.network_status !== "approved" ? "bg-sand-100 text-ink/55" : "bg-sand-100"}`}>
                    {linkLabel(v)}
                  </span>
                  {v.invite_held && (
                    <span data-testid={`vendor-row-held-${v.id}`} className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 font-semibold text-indigo-700">
                      <Clock className="h-3 w-3" /> Invite goes out {v.invite_held.send_at_local}
                    </span>
                  )}
                  {(v.trades || []).map((t) => <span key={t}>{CAT_LABEL[t] || t}</span>)}
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs font-semibold">
                {v.coi_ok ? <span className="inline-flex items-center gap-1 text-emerald-700"><ShieldCheck className="h-3.5 w-3.5" />Insured</span>
                  : <span className="inline-flex items-center gap-1 text-danger-600"><ShieldAlert className="h-3.5 w-3.5" />Not insured</span>}
                <span className="text-ink/40">{v.routable_crew} crew ready</span>
              </div>
              {(warningsByVendor[v.id] || []).filter((w) => w.kind !== "coi").length > 0 && (
                <div data-testid={`vendor-attention-${v.id}`} className="mt-1 w-full space-y-1 border-t border-sand-100 pt-2">
                  {(warningsByVendor[v.id] || []).filter((w) => w.kind !== "coi").map((w, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-xs font-medium text-amber-700">
                      <AlertTriangle className="h-3 w-3 shrink-0" /> {w.detail}
                    </div>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {tab === "today" && (
        <div className="space-y-6">
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/45">
              <CalendarClock className="h-3.5 w-3.5" /> Today · {ov?.today}
            </div>
            {(ov?.visits || []).length === 0 ? (
              <div className="rounded-xl border border-dashed border-sand-300 py-10 text-center text-sm text-ink/45" data-testid="visits-empty">
                Nothing scheduled today.
              </div>
            ) : (ov.visits || []).map((v) => (
              <div key={v.id} data-testid={`visit-row-${v.id}`} className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sand-200 bg-white px-4 py-3">
                <div>
                  <div className="text-sm font-semibold text-ink">{v.name}</div>
                  <div className="text-xs text-ink/45">{v.window?.start}–{v.window?.end} · {(v.photos || []).length} photo(s)</div>
                </div>
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${v.status === "done" ? "bg-emerald-50 text-emerald-700" : v.status === "missed" ? "bg-red-50 text-red-700" : v.status === "checked_in" ? "bg-indigo-50 text-indigo-700" : "bg-sand-200 text-ink/60"}`}>
                  {v.status === "checked_in" ? "On site" : v.status}
                </span>
              </div>
            ))}
          </div>
          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-wider text-ink/45">Recurring schedules</div>
            {schedules.filter((s) => s.active !== false).map((s) => (
              <div key={s.id} data-testid={`sched-row-${s.id}`} className="mb-2 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-sand-200 bg-white px-4 py-3">
                <div>
                  <div className="text-sm font-semibold text-ink">{s.name}</div>
                  <div className="text-xs text-ink/45">
                    {(s.dow || []).map((d) => DOW[d]).join(", ")} · {s.start}–{s.end}
                    {s.photo_required ? " · photos required" : ""} · {(s.checklist || []).length} checklist items
                    {s.paused_reason === "coi_expired" ? " · PAUSED, insurance lapsed" : ""}
                  </div>
                </div>
                {!readOnly && (
                  <button data-testid={`sched-del-${s.id}`} onClick={async () => {
                    await api.delete(`/tenants/${tenantId}/field-services/schedules/${s.id}`); toast.success("Removed"); load();
                  }} className="rounded-lg p-1.5 text-ink/30 hover:bg-danger-50 hover:text-danger-600"><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
            ))}
            {!readOnly && (
              <button data-testid="sched-open-wizard" onClick={() => setSchedOpen(true)}
                className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-ink px-4 py-2.5 text-sm font-bold text-white hover:bg-ink/90">
                <Plus className="h-4 w-4" /> Set up a recurring vendor
              </button>
            )}
          </div>
        </div>
      )}

      {tab === "spine" && <WorkOrders key={`wo-${woRefresh}`} tenantId={tenantId} readOnly={readOnly}
        onGoTo={setTab} />}

      {tab === "queue" && <VisitQueue tenantId={tenantId} readOnly={readOnly} />}

      {tab === "invoices" && (
        <Invoices tenantId={tenantId} readOnly={readOnly}
          canDecide={role === "board_member" || role === "super_admin"} />
      )}

      {tab === "projects" && (
        <FieldProjects tenantId={tenantId} vendors={vendors} readOnly={readOnly}
          canDecide={role === "board_member" || !readOnly}
          canAuthor={role === "board_member" || !readOnly}
          canBoardSign={["board_member", "super_admin", "platform_admin", "operator"].includes(role)}
          autoOpenNew={pendingNewProject} onAutoOpened={() => setPendingNewProject(false)}
          onAwarded={() => setTab("spine")} />
      )}

      {tab === "find" && <FindVendor />}

      {tab === "settings" && cfg && (
        <fieldset disabled={readOnly} className="space-y-6" data-testid="fs-settings">
          {readOnly && (
            <p className="rounded-xl border border-sand-200 bg-sand-100 px-4 py-3 text-xs font-semibold text-ink/60" data-testid="fs-readonly-note">
              {V.Grp()} view — you can read every setting here, but only {V.a("org")} manager can change them.
            </p>
          )}
          <AccountingSettings tenantId={tenantId} readOnly={readOnly} companyId={tenant?.company_id} />
          <CollapsibleCard id="fs.basics" icon={MapPin} title="Service area" testid="fs-settings-basics"
            summary={cfg.market ? `${cfg.market}${cfg.market_source === "profile" ? ` · from the ${V.one("org")} Profile` : ""}` : `no address on the ${V.one("org")} Profile yet`}>
            <p className="mb-2 text-xs text-ink/55">
              Used to show you approved companies that serve your area. It comes from the property address on the
              {V.one("org")} Profile{cfg.market_source === "profile" ? "" : cfg.market_source === "custom" ? " — you've set a different one here" : " — add the address there and this fills itself in"}.
              Only change it here if the {V.lc("org")} is served from somewhere else.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <input data-testid="fs-market-input" defaultValue={cfg.market_source === "custom" ? cfg.market : ""} maxLength={120} disabled={readOnly}
                placeholder={cfg.market_source === "profile" ? `${cfg.market} (from the ${V.one("org")} Profile)` : "e.g. Austin, TX"}
                onBlur={(e) => { const v = e.target.value.trim(); if (v !== (cfg.market_source === "custom" ? cfg.market : "")) saveCfg({ market: v }); }}
                className="w-full max-w-sm rounded-xl border border-sand-300 px-3.5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              {cfg.market_source === "custom" && !readOnly && (
                <button type="button" data-testid="fs-market-reset" onClick={() => saveCfg({ market: "" })}
                  className="rounded-xl border border-sand-300 bg-white px-3.5 py-2.5 text-sm font-bold text-ink/70 hover:border-ink/30">Use the {V.one("org")} Profile address</button>
              )}
            </div>
          </CollapsibleCard>
          <CollapsibleCard id="fs.timing" icon={Clock} title="Timing & follow-up" testid="fs-settings-timing" defaultOpen={false}
            summary={`office after ${cfg.ack_dispatcher_min} min · you after ${cfg.ack_manager_min} min · ${cfg.sla_hours_default}h normal / ${cfg.sla_hours_urgent}h urgent`}>
          <div className="mt-4">
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/45">
              <Clock className="h-3.5 w-3.5" /> Nobody accepted the job
            </div>
            <div className="space-y-2">
              <Num label="Then tell the vendor's office" hint="The crew member gets it immediately; the office is only copied until this point." value={cfg.ack_dispatcher_min} onChange={(v) => saveCfg({ ack_dispatcher_min: v })} testid="cfg-ack-dispatcher" />
              <Num label="Then tell me" hint={`You're the last rung — you find out before the ${V.lc("person")} has to ask again.`} value={cfg.ack_manager_min} onChange={(v) => saveCfg({ ack_manager_min: v })} testid="cfg-ack-manager" />
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-ink/45">
              <CheckCircle2 className="h-3.5 w-3.5" /> Accepted, but never closed out
            </div>
            <div className="space-y-2">
              <Num label="Grace after their ETA" hint="Before we nudge, give them a little room." value={cfg.completion_grace_min} onChange={(v) => saveCfg({ completion_grace_min: v })} testid="cfg-grace" />
              <Num label="Second nudge after the first" value={cfg.completion_nudge2_min} onChange={(v) => saveCfg({ completion_nudge2_min: v })} testid="cfg-nudge2" />
              <Num label="Tell the office after the deadline" value={cfg.completion_dispatcher_min} onChange={(v) => saveCfg({ completion_dispatcher_min: v })} testid="cfg-comp-dispatcher" />
              <Num label="Tell me after the deadline" value={cfg.completion_manager_min} onChange={(v) => saveCfg({ completion_manager_min: v })} testid="cfg-comp-manager" />
              <Num label="Default time to finish a normal job" value={cfg.sla_hours_default} onChange={(v) => saveCfg({ sla_hours_default: v })} testid="cfg-sla-default" suffix="hrs" />
              <Num label="Default time to finish an urgent job" value={cfg.sla_hours_urgent} onChange={(v) => saveCfg({ sla_hours_urgent: v })} testid="cfg-sla-urgent" suffix="hrs" />
            </div>
          </div>
          </CollapsibleCard>
          <CollapsibleCard id="fs.rules" icon={CheckCircle2} title="Rules" testid="fs-settings-rules" defaultOpen={false}
            summary={`${cfg.coi_hard_block ? "uninsured blocked" : "uninsured allowed"} · ${cfg.reassure_resident ? `${V.lcs("person")} kept posted` : "no late texts"}`}>
          <div className="mt-4 space-y-2">
            <Toggle checked={cfg.reassure_resident} onChange={(v) => saveCfg({ reassure_resident: v })} testid="cfg-reassure"
              label={`Keep the ${V.lc("person")} posted when things run late`}
              desc="A “running a little behind, still on it” text. Silence is what makes people call your office." />
            <Toggle checked={cfg.coi_hard_block} onChange={(v) => saveCfg({ coi_hard_block: v })} testid="cfg-coi-block"
              label="Refuse to dispatch to an uninsured vendor"
              desc="Not a warning banner — the router simply won't send the job. Strongly recommended." />
            <Toggle checked={cfg.bid_without_paperwork} onChange={(v) => saveCfg({ bid_without_paperwork: v })} testid="cfg-bid-without-paperwork"
              label="Take a price before the paperwork is complete"
              desc="A company you just met can read the brief and send you a number the same day. Their proposal shows as “paperwork outstanding”, we chase the documents for them, and nothing can be handed to them until all three land. Turn this off and no price can be submitted until we hold insurance, a W-9 and a signed agreement." />
            <Num label="Treat two reports as the same job within" hint="A second photo of the same mess is not a second job." value={cfg.dedupe_window_hours} onChange={(v) => saveCfg({ dedupe_window_hours: v })} testid="cfg-dedupe" suffix="hrs" />
            <Num label="Ask vendors to re-confirm their crew list every" value={cfg.roster_confirm_days} onChange={(v) => saveCfg({ roster_confirm_days: v })} testid="cfg-roster-days" suffix="days" />
            <Num label="Chase a project nobody has priced after" hint="Counted from the day you posted it, so a project with no deadline still gets chased. One alert, then it sits in amber on Work orders until you act." value={cfg.project_stale_days} onChange={(v) => saveCfg({ project_stale_days: v })} testid="cfg-project-stale" suffix="days" />
            <Num label="Stop chasing a half-finished vendor after" hint="We remind their office on day 1, 3, 7 and then this day. After that we stop asking and flag them to you — nagging forever is its own kind of broken." value={cfg.vendor_chase_stop_days} onChange={(v) => saveCfg({ vendor_chase_stop_days: v })} testid="cfg-vendor-chase" suffix="days" />
            <Num label="Archive a vendor who never finished after" hint="Leave it at 0 and we never archive on our own — we just flag them, and you decide. Archiving deletes nothing; you can bring them back from the vendor list." value={cfg.vendor_auto_archive_days} onChange={(v) => saveCfg({ vendor_auto_archive_days: v })} testid="cfg-vendor-archive" suffix="days · 0 = never" />
          </div>
          </CollapsibleCard>
          <p className="flex items-start gap-2 rounded-xl bg-sand-100 px-4 py-3 text-xs leading-relaxed text-ink/55">
            <Settings2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Nothing here ever auto-closes a job. Only the crew marking it done, or the {V.lc("person")} confirming,
            closes anything — so your completion numbers mean what they say.
          </p>
        </fieldset>
      )}

      <Dialog open={newJobOpen} onOpenChange={setNewJobOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Open a job</DialogTitle>
            <DialogDescription>Something that needs doing now — usually someone on the phone.</DialogDescription>
          </DialogHeader>
          {newJobOpen && <NewJobWizard tenantId={tenantId} onClose={() => setNewJobOpen(false)}
            onDone={() => { load(); setWoRefresh((n) => n + 1); }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Invite a vendor</DialogTitle></DialogHeader>
          <InviteVendor tenantId={tenantId} communityName={tenant?.name || `Your ${V.lc("org")}`} onDone={load} onClose={() => setInviteOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto">
          <DialogHeader><DialogTitle>Add a vendor manually</DialogTitle></DialogHeader>
          <ManualAddVendor mode="community" tenantId={tenantId} onDone={load} onClose={() => setManualOpen(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!openVendor} onOpenChange={(o) => !o && setOpenVendor(null)}>
        <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto">
          <DialogHeader><DialogTitle>Vendor</DialogTitle></DialogHeader>
          {openVendor && <VendorPanel tenantId={tenantId} vendorId={openVendor} readOnly={readOnly} onClose={() => setOpenVendor(null)} onChanged={load} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle className="pr-6 break-words leading-snug">{detail?.wo?.ref} · {detail?.wo?.summary}</DialogTitle></DialogHeader>
          {detail && (
            <div className="min-w-0 space-y-4" data-testid="wo-detail">
              <div className="flex flex-wrap items-center gap-2">
                <Pill status={detail.wo.status} />
                <span className="text-xs text-ink/50">{detail.wo.location} · {CAT_LABEL[detail.wo.category] || detail.wo.category}</span>
                {detail.wo.proof_missing && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-800">No photo on file</span>}
              </div>
              <WoJourney wo={detail.wo} />
              <div className="grid gap-2 text-xs text-ink/60 sm:grid-cols-2">
                <div>Vendor: <span className="font-semibold text-ink">{detail.wo.vendor_name || "—"}</span></div>
                <div>Assigned to: <span className="font-semibold text-ink">{detail.wo.tech_name || "—"}</span></div>
                <div>Routed: <span className="font-semibold text-ink">{ROUTED_LABEL[detail.wo.routed_via] || "not dispatched yet"}</span></div>
                <div>Deadline: <span className="font-semibold text-ink">{fmt(detail.wo.completion_deadline) || "—"}</span></div>
              </div>
              {((detail.wo.before_urls || []).length > 0 || (detail.wo.after_urls || []).length > 0) && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {[["Before", detail.wo.before_urls], ["After", detail.wo.after_urls]].map(([label, urls]) => (
                    <div key={label}>
                      <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-ink/45"><Camera className="h-3 w-3" />{label}</div>
                      <div className="flex flex-wrap gap-2">
                        {(urls || []).length === 0 && <span className="text-xs text-ink/35">none</span>}
                        {(urls || []).map((u, i) => <img key={i} src={u} alt={label} className="h-24 w-24 rounded-lg object-cover" />)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <div className="mb-1.5 text-xs font-bold uppercase tracking-wider text-ink/45">Everything that happened</div>
                <div className="space-y-1.5">
                  {(detail.wo.timeline || []).map((t, i) => (
                    <div key={i} className="flex flex-col gap-0.5 border-b border-sand-100 pb-1.5 text-xs last:border-0 last:pb-0 sm:flex-row sm:gap-2 sm:border-0 sm:pb-0">
                      <span className="shrink-0 font-mono text-ink/35 sm:w-28">{fmt(t.at)}</span>
                      <span className="min-w-0 break-words">
                        <span className="font-semibold text-ink/70">{t.event}</span>{" "}
                        <span className="text-ink/50">{t.detail}{t.actor ? ` · ${t.actor}` : ""}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              {!readOnly && (
              <div className="flex flex-col gap-2 border-t border-sand-200 pt-3 sm:flex-row sm:flex-wrap sm:items-center">
                {["closed", "cancelled"].includes(detail.wo.status) ? (
                  <span className="text-xs font-semibold text-ink/45" data-testid="wo-ended-note">
                    This job is {detail.wo.status === "closed" ? "closed" : "cancelled"} — nothing left to do here.
                  </span>
                ) : (
                  <>
                    <button data-testid="wo-reclaim" onClick={() => woOp(detail.id, "reclaim")}
                      className="w-full rounded-xl border border-sand-300 px-4 py-2 text-sm font-semibold hover:border-ink/30 sm:w-auto">Take it back &amp; re-dispatch</button>
                    <button data-testid="wo-redispatch" onClick={() => woOp(detail.id, "redispatch")}
                      className="w-full rounded-xl border border-sand-300 px-4 py-2 text-sm font-semibold hover:border-ink/30 sm:w-auto">Re-send to crew</button>
                    <button data-testid="wo-complete" onClick={() => woOp(detail.id, "complete", { note: "closed by manager" })}
                      className="w-full rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 sm:w-auto">Mark completed</button>
                    <button data-testid="wo-close" onClick={() => woOp(detail.id, "close")}
                      className="w-full rounded-xl bg-ink px-4 py-2 text-sm font-bold text-white hover:bg-ink/90 sm:w-auto">Close</button>
                  </>
                )}
              </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="max-w-3xl" data-testid="sched-wizard-dialog">
          <DialogHeader>
            <DialogTitle>Set up a recurring vendor</DialogTitle>
            <DialogDescription>A company that comes on set days — this creates the standing work order its visits hang off.</DialogDescription>
          </DialogHeader>
          {schedOpen && (
            <RecurringVendorWizard tenantId={tenantId} vendors={vendors} glCodes={glCodes}
              onDone={load} onClose={() => setSchedOpen(false)} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
